import React, { useEffect, useState } from 'react';
import { Sparkles, Save, Plus, Trash2, GripVertical, RotateCcw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon options for "Every Deal Is" cards (keep in sync with EVERY_DEAL_ICONS in BespokeSourcingPage.jsx)
const EVERY_DEAL_ICON_OPTIONS = ['Target', 'Users', 'ShieldCheck', 'Gem', 'BadgeCheck', 'Layers', 'Scale', 'Award', 'Shield', 'Sparkles', 'Check', 'Compass', 'Briefcase'];

// Available icon names (must match PROCESS_ICONS in BespokeSourcingPage.jsx)
const ICON_OPTIONS = [
  'FileText', 'Lock', 'Search', 'Award', 'ClipboardCheck', 'Calendar',
  'Handshake', 'TrendingUp', 'Sparkles', 'Shield', 'Compass', 'Briefcase',
  'Target', 'Building2',
];

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold">{label}</span>
    {hint && <span className="block text-white/30 text-[11px] mt-0.5 normal-case tracking-normal">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm focus:outline-none transition-colors ${props.className || ''}`}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={`w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm focus:outline-none transition-colors resize-none ${props.className || ''}`}
  />
);

export const BespokeSourcingTab = ({ getStoredPassword }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-sourcing/settings?password=${getStoredPassword()}`);
      if (r.ok) setSettings(await r.json());
      else toast.error('Could not load Bespoke settings');
    } catch { toast.error('Network error'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-sourcing/settings?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hero_eyebrow: settings.hero_eyebrow,
          hero_title_pre: settings.hero_title_pre,
          hero_title_italic: settings.hero_title_italic,
          hero_title_post: settings.hero_title_post,
          hero_subtitle: settings.hero_subtitle,
          hero_tagline: settings.hero_tagline,
          process_title: settings.process_title,
          process_subtitle: settings.process_subtitle,
          process_steps: (settings.process_steps || []).map(s => ({
            icon: s.icon, title: s.title, description: s.description,
          })),
          // Commitment section
          commitment_enabled: !!settings.commitment_enabled,
          commitment_eyebrow: settings.commitment_eyebrow,
          every_deal_title: settings.every_deal_title,
          every_deal_points: (settings.every_deal_points || [])
            .map(p => (typeof p === 'string' ? { icon: 'Target', text: p } : p))
            .filter(p => p && (p.text || '').trim()),
          every_deal_footer: settings.every_deal_footer,
          commitment_process_title: settings.commitment_process_title,
          commitment_process_intro: settings.commitment_process_intro,
          deposit_amount: settings.deposit_amount,
          deposit_currency: settings.deposit_currency,
          deposit_label: settings.deposit_label,
          deposit_period_weeks: settings.deposit_period_weeks,
          this_allows_title: settings.this_allows_title,
          this_allows_points: (settings.this_allows_points || []).filter(p => p && p.trim()),
          refund_text: settings.refund_text,
        }),
      });
      if (r.ok) toast.success('Bespoke Sourcing settings saved');
      else toast.error('Save failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset all Bespoke Sourcing copy + steps to factory defaults? Your live customisations will be lost.')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/bespoke-sourcing/settings?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hero_eyebrow: null, hero_title_pre: null, hero_title_italic: null,
          hero_title_post: null, hero_subtitle: null, hero_tagline: null,
          process_title: null, process_subtitle: null, process_steps: null,
        }),
      });
      // Then refetch defaults
      await load();
      toast.success('Reset to defaults');
    } catch { toast.error('Network error'); }
  };

  const setField = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));
  const updateStep = (idx, k, v) => {
    setSettings(prev => ({
      ...prev,
      process_steps: prev.process_steps.map((s, i) => i === idx ? { ...s, [k]: v } : s),
    }));
  };
  const addStep = () => {
    setSettings(prev => ({
      ...prev,
      process_steps: [
        ...prev.process_steps,
        { icon: 'FileText', title: 'New Step', description: 'Edit this step description.' },
      ],
    }));
  };
  const removeStep = (idx) => {
    if (!window.confirm('Remove this step?')) return;
    setSettings(prev => ({
      ...prev,
      process_steps: prev.process_steps.filter((_, i) => i !== idx),
    }));
  };
  const moveStep = (idx, dir) => {
    setSettings(prev => {
      const arr = [...prev.process_steps];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...prev, process_steps: arr };
    });
  };

  if (!settings) return <div className="text-white/50 text-sm">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl text-white font-light flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" /> Bespoke Sourcing Page
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Edit the hero copy and the "How It Works" process steps that appear on{' '}
            <a href="/bespoke-sourcing" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] inline-flex items-center gap-1 hover:underline">
              /bespoke-sourcing <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={resetDefaults} variant="outline" className="border-white/15 text-white/60 hover:text-white hover:bg-white/5" data-testid="bespoke-reset-btn">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="bespoke-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* HERO */}
      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <div>
          <h3 className="text-white font-semibold text-sm mb-1">Hero Section</h3>
          <p className="text-white/40 text-[11px]">Top of the page — eyebrow, title, subtitle, tagline.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow (gold caps text above title)">
            <Input value={settings.hero_eyebrow || ''} onChange={e => setField('hero_eyebrow', e.target.value)} data-testid="bespoke-hero-eyebrow" />
          </Field>
          <Field label="Title — Italic Word" hint="The single word styled in italic + gold inside the title">
            <Input value={settings.hero_title_italic || ''} onChange={e => setField('hero_title_italic', e.target.value)} data-testid="bespoke-hero-italic" />
          </Field>
          <Field label="Title — Before Italic">
            <Input value={settings.hero_title_pre || ''} onChange={e => setField('hero_title_pre', e.target.value)} placeholder="Tell us what " data-testid="bespoke-hero-pre" />
          </Field>
          <Field label="Title — After Italic">
            <Input value={settings.hero_title_post || ''} onChange={e => setField('hero_title_post', e.target.value)} placeholder=" are looking for." data-testid="bespoke-hero-post" />
          </Field>
        </div>

        <Field label="Subtitle (paragraph under title)">
          <Textarea rows={3} value={settings.hero_subtitle || ''} onChange={e => setField('hero_subtitle', e.target.value)} data-testid="bespoke-hero-subtitle" />
        </Field>

        <Field label="Tagline (small caps under subtitle)">
          <Input value={settings.hero_tagline || ''} onChange={e => setField('hero_tagline', e.target.value)} data-testid="bespoke-hero-tagline" />
        </Field>
      </section>

      {/* PROCESS STEPS */}
      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-white font-semibold text-sm mb-1">"How It Works" Process Steps</h3>
            <p className="text-white/40 text-[11px]">Edit, reorder, add or remove the bespoke sourcing process steps.</p>
          </div>
          <Button onClick={addStep} variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid="bespoke-add-step">
            <Plus className="w-4 h-4 mr-1" /> Add Step
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Section Title">
            <Input value={settings.process_title || ''} onChange={e => setField('process_title', e.target.value)} data-testid="bespoke-process-title" />
          </Field>
          <Field label="Section Subtitle">
            <Input value={settings.process_subtitle || ''} onChange={e => setField('process_subtitle', e.target.value)} data-testid="bespoke-process-subtitle" />
          </Field>
        </div>

        <div className="space-y-3 pt-2">
          {(settings.process_steps || []).map((s, idx) => (
            <div
              key={idx}
              className="bg-[#0a1a14] border border-white/10 rounded-lg p-4 space-y-3"
              data-testid={`bespoke-step-${idx}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold">Step {String(idx + 1).padStart(2, '0')}</p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveStep(idx, -1)}
                    disabled={idx === 0}
                    className="text-white/40 hover:text-white p-1 rounded disabled:opacity-25 disabled:cursor-not-allowed"
                    title="Move up"
                  >↑</button>
                  <button
                    onClick={() => moveStep(idx, 1)}
                    disabled={idx >= settings.process_steps.length - 1}
                    className="text-white/40 hover:text-white p-1 rounded disabled:opacity-25 disabled:cursor-not-allowed"
                    title="Move down"
                  >↓</button>
                  <button
                    onClick={() => removeStep(idx)}
                    className="text-white/40 hover:text-red-400 p-1 rounded ml-1"
                    title="Remove step"
                    data-testid={`bespoke-step-${idx}-remove`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Icon">
                  <select
                    value={s.icon || 'FileText'}
                    onChange={e => updateStep(idx, 'icon', e.target.value)}
                    className="w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm focus:outline-none cursor-pointer"
                    data-testid={`bespoke-step-${idx}-icon`}
                  >
                    {ICON_OPTIONS.map(i => <option key={i} value={i} className="bg-[#0a1a14]">{i}</option>)}
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Title">
                    <Input value={s.title || ''} onChange={e => updateStep(idx, 'title', e.target.value)} data-testid={`bespoke-step-${idx}-title`} />
                  </Field>
                </div>
              </div>

              <Field label="Description">
                <Textarea rows={2} value={s.description || ''} onChange={e => updateStep(idx, 'description', e.target.value)} data-testid={`bespoke-step-${idx}-desc`} />
              </Field>
            </div>
          ))}
        </div>
      </section>

      {/* COMMITMENT / DEPOSIT SECTION */}
      <section className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-5" data-testid="bespoke-commitment-editor">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-white font-semibold text-sm mb-1">Commitment & Refundable Deposit</h3>
            <p className="text-white/40 text-[11px]">Builds trust by showing how every deal is reviewed + the refundable deposit terms.</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.commitment_enabled}
              onChange={e => setField('commitment_enabled', e.target.checked)}
              className="accent-[#D4AF37] w-4 h-4"
              data-testid="bespoke-commitment-enabled"
            />
            Show on page
          </label>
        </div>

        <Field label="Section Eyebrow">
          <Input value={settings.commitment_eyebrow || ''} onChange={e => setField('commitment_eyebrow', e.target.value)} data-testid="bespoke-commitment-eyebrow" />
        </Field>

        {/* Every deal is */}
        <div className="space-y-3 border-t border-white/10 pt-5">
          <Field label='"Every Deal Is" — Heading'>
            <Input value={settings.every_deal_title || ''} onChange={e => setField('every_deal_title', e.target.value)} data-testid="bespoke-every-deal-title" />
          </Field>

          <div>
            <span className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold">Points</span>
            <div className="space-y-2 mt-2">
              {(settings.every_deal_points || []).map((p, i) => {
                // Back-compat: legacy entries are plain strings; new entries are {icon, text}
                const isObj = p && typeof p === 'object';
                const text = isObj ? (p.text || '') : (p || '');
                const icon = isObj ? (p.icon || 'Target') : ['Target', 'Users', 'ShieldCheck'][i] || 'Target';
                const updatePoint = (next) => setField('every_deal_points', (settings.every_deal_points || []).map((x, j) => j === i ? next : x));
                return (
                  <div key={i} className="flex items-center gap-2 bg-[#0a1a14]/40 rounded-md p-1">
                    <span className="text-[#D4AF37]/60 text-xs w-5 text-center">{i + 1}.</span>
                    <select
                      value={icon}
                      onChange={e => updatePoint({ icon: e.target.value, text })}
                      className="bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-2 py-2 text-white text-xs focus:outline-none cursor-pointer w-32"
                      data-testid={`bespoke-every-deal-point-${i}-icon`}
                    >
                      {EVERY_DEAL_ICON_OPTIONS.map(opt => (
                        <option key={opt} value={opt} className="bg-[#0a1a14]">{opt}</option>
                      ))}
                    </select>
                    <Input
                      value={text}
                      onChange={e => updatePoint({ icon, text: e.target.value })}
                      data-testid={`bespoke-every-deal-point-${i}`}
                    />
                    <button
                      onClick={() => setField('every_deal_points', settings.every_deal_points.filter((_, j) => j !== i))}
                      className="text-white/40 hover:text-red-400 p-2"
                      title="Remove point"
                      data-testid={`bespoke-every-deal-point-${i}-remove`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
              <Button
                onClick={() => setField('every_deal_points', [...(settings.every_deal_points || []), { icon: 'Target', text: '' }])}
                variant="outline"
                size="sm"
                className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 mt-2"
                data-testid="bespoke-every-deal-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Point
              </Button>
            </div>
          </div>

          <Field label="Footer Line (small italic note under points)">
            <Input value={settings.every_deal_footer || ''} onChange={e => setField('every_deal_footer', e.target.value)} data-testid="bespoke-every-deal-footer" />
          </Field>
        </div>

        {/* Process callout */}
        <div className="space-y-3 border-t border-white/10 pt-5">
          <Field label="Process Title">
            <Input value={settings.commitment_process_title || ''} onChange={e => setField('commitment_process_title', e.target.value)} data-testid="bespoke-process-callout-title" />
          </Field>

          <Field label="Process Intro Paragraph">
            <Textarea rows={2} value={settings.commitment_process_intro || ''} onChange={e => setField('commitment_process_intro', e.target.value)} data-testid="bespoke-process-callout-intro" />
          </Field>

          {/* Deposit */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Deposit Amount">
              <Input
                type="number"
                value={settings.deposit_amount ?? ''}
                onChange={e => setField('deposit_amount', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                data-testid="bespoke-deposit-amount"
              />
            </Field>
            <Field label="Currency">
              <select
                value={settings.deposit_currency || 'GBP'}
                onChange={e => setField('deposit_currency', e.target.value)}
                className="w-full bg-[#0a1a14] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/50 rounded-md px-3 py-2 text-white text-sm focus:outline-none cursor-pointer"
                data-testid="bespoke-deposit-currency"
              >
                <option value="GBP" className="bg-[#0a1a14]">£ GBP</option>
                <option value="USD" className="bg-[#0a1a14]">$ USD</option>
                <option value="EUR" className="bg-[#0a1a14]">€ EUR</option>
                <option value="AED" className="bg-[#0a1a14]">د.إ AED</option>
                <option value="GHS" className="bg-[#0a1a14]">₵ GHS</option>
              </select>
            </Field>
            <Field label="Search Period (weeks)">
              <Input
                type="number"
                value={settings.deposit_period_weeks ?? ''}
                onChange={e => setField('deposit_period_weeks', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                data-testid="bespoke-deposit-weeks"
              />
            </Field>
          </div>

          <Field label="Deposit Label (text under amount)">
            <Input value={settings.deposit_label || ''} onChange={e => setField('deposit_label', e.target.value)} data-testid="bespoke-deposit-label" />
          </Field>
        </div>

        {/* This allows us to */}
        <div className="space-y-3 border-t border-white/10 pt-5">
          <Field label='"This Allows Us To" — Heading'>
            <Input value={settings.this_allows_title || ''} onChange={e => setField('this_allows_title', e.target.value)} data-testid="bespoke-allows-title" />
          </Field>

          <div>
            <span className="text-white/55 text-[10px] uppercase tracking-[0.18em] font-semibold">Points</span>
            <div className="space-y-2 mt-2">
              {(settings.this_allows_points || []).map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[#D4AF37]/60 text-xs w-5">{i + 1}.</span>
                  <Input
                    value={p}
                    onChange={e => setField('this_allows_points', settings.this_allows_points.map((x, j) => j === i ? e.target.value : x))}
                    data-testid={`bespoke-allows-point-${i}`}
                  />
                  <button
                    onClick={() => setField('this_allows_points', settings.this_allows_points.filter((_, j) => j !== i))}
                    className="text-white/40 hover:text-red-400 p-2"
                    title="Remove point"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <Button
                onClick={() => setField('this_allows_points', [...(settings.this_allows_points || []), ''])}
                variant="outline"
                size="sm"
                className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 mt-2"
                data-testid="bespoke-allows-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Point
              </Button>
            </div>
          </div>

          <Field label="Refund Guarantee Text">
            <Textarea rows={2} value={settings.refund_text || ''} onChange={e => setField('refund_text', e.target.value)} data-testid="bespoke-refund-text" />
          </Field>
        </div>
      </section>

      {/* Footer save */}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="bespoke-save-btn-bottom">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

